import React, { useState } from 'react';
import { useAuth } from './AuthContext';
import {validateLogin, validateNewUser} from "./components/user/UserValidator";

const LoginScreen = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    fullName: '',
    phone: '',
    company: '',
    location: '',
    remember_me: false
  });
  // supplierIds managed separately as array of numbers
  const [supplierIds, setSupplierIds] = useState([]);
  const [supplierInput, setSupplierInput] = useState('');
  const [supplierInputError, setSupplierInputError] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const { login, register } = useAuth();

  const handleAddSupplierId = () => {
    const trimmed = supplierInput.trim();
    if (!trimmed) return;
    const num = parseInt(trimmed, 10);
    if (isNaN(num) || num < 1 || num > 999999 || String(num) !== trimmed) {
      setSupplierInputError('Bitte eine gültige Zahl zwischen 1 und 999999 eingeben');
      return;
    }
    if (supplierIds.includes(num)) {
      setSupplierInputError('Diese ID wurde bereits hinzugefügt');
      return;
    }
    setSupplierIds(prev => [...prev, num]);
    setSupplierInput('');
    setSupplierInputError('');
    if (errors.supplierIds) {
      setErrors(prev => ({ ...prev, supplierIds: '' }));
    }
  };

  const handleSupplierInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddSupplierId();
    }
  };

  const handleRemoveSupplierId = (id) => {
    setSupplierIds(prev => prev.filter(s => s !== id));
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = isLogin ? validateLogin(formData) : validateNewUser(formData, supplierIds);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    
    try {
      let result;
      if (isLogin) {
        result = await login({
          username: formData.username,
          password: formData.password,
          remember_me: formData.remember_me
        });
      } else {
        result = await register({
          username: formData.username,
          email: formData.email,
          password: formData.password,
          fullName: formData.fullName,
          phone: formData.phone || null,
          company: formData.company || null,
          location: formData.location || null,
          supplierIds: supplierIds,
        });
      }

      if (!result.success) {
        setErrors({ general: result.error });
      }
    } catch (error) {
      setErrors({ general: 'Ein unerwarteter Fehler ist aufgetreten' });
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
    setErrors({});
    setSupplierIds([]);
    setSupplierInput('');
    setSupplierInputError('');
    setFormData(prev => ({
      username: '',
      email: '',
      password: '',
      fullName: '',
      phone: '',
      company: '',
      location: ''
    }));
  };

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header with Logo */}
          <div className="text-center mb-6">
            <img 
              src="/fastplanet-logo.svg" 
              alt="FASTPLANET" 
              className="h-12 mx-auto mb-4"
            />
            <h2 className="text-2xl font-bold text-gray-900">
              {isLogin ? 'Anmelden' : 'Registrieren'}
            </h2>
            <p className="text-gray-600 mt-2">
              {isLogin 
                ? 'Melden Sie sich bei Ihrem Konto an'
                : 'Erstellen Sie ein neues Konto'
              }
            </p>
          </div>

          {errors.general && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
              <p className="text-sm text-red-600">{errors.general}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Username */}
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
                Benutzername *
              </label>
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                  errors.username ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Ihr Benutzername"
              />
              {errors.username && <p className="text-xs text-red-600 mt-1">{errors.username}</p>}
            </div>

            {/* Registration fields */}
            {!isLogin && (
              <>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    E-Mail *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                      errors.email ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="ihre@email.de"
                  />
                  {errors.email && <p className="text-xs text-red-600 mt-1">{errors.email}</p>}
                </div>

                <div>
                  <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1">
                    Vollständiger Name *
                  </label>
                  <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                      errors.fullName ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Max Mustermann"
                  />
                  {errors.fullName && <p className="text-xs text-red-600 mt-1">{errors.fullName}</p>}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                      Telefon
                    </label>
                    <input
                      type="text"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                        errors.phone ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="+49 30 12345678"
                    />
                    {errors.phone && <p className="text-xs text-red-600 mt-1">{errors.phone}</p>}
                  </div>

                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
                      Firma
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      placeholder="FastPlanet GmbH"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-1">
                    Standort
                  </label>
                  <input
                    type="text"
                    id="location"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    placeholder="Berlin"
                  />
                </div>

                {/* Supplier IDs */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Lieferanten-IDs *
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      inputMode="numeric"
                      value={supplierInput}
                      onChange={e => { setSupplierInput(e.target.value.replace(/\D/g, '')); setSupplierInputError(''); }}
                      onKeyDown={handleSupplierInputKeyDown}
                      className={`flex-1 px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                        supplierInputError || errors.supplierIds ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="ID eingeben (z. B. 42)"
                    />
                    <button
                      type="button"
                      onClick={handleAddSupplierId}
                      className="px-3 py-2 bg-orange-100 hover:bg-orange-200 text-orange-700 font-medium rounded-md border border-orange-300 text-sm"
                    >
                      + Add
                    </button>
                  </div>
                  {supplierInputError && <p className="text-xs text-red-600 mt-1">{supplierInputError}</p>}
                  {errors.supplierIds && <p className="text-xs text-red-600 mt-1">{errors.supplierIds}</p>}
                  {supplierIds.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {supplierIds.map(id => (
                        <span
                          key={id}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-orange-50 border border-orange-200 text-orange-800 text-xs rounded-full"
                        >
                          #{id}
                          <button
                            type="button"
                            onClick={() => handleRemoveSupplierId(id)}
                            className="text-orange-400 hover:text-orange-700 leading-none"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    IDs der Lieferanten, denen Sie zugeordnet sind.
                  </p>
                </div>
              </>
            )}

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Passwort *
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                  errors.password ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Ihr Passwort"
              />
              {errors.password && <p className="text-xs text-red-600 mt-1">{errors.password}</p>}
            </div>

            {/* Remember Me (Login only) */}
            {isLogin && (
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="remember_me"
                  name="remember_me"
                  checked={formData.remember_me}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                />
                <label htmlFor="remember_me" className="ml-2 block text-sm text-gray-700">
                  Angemeldet bleiben (bis zu 2 Stunden)
                </label>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {isLogin ? 'Anmelden...' : 'Registrieren...'}
                </div>
              ) : (
                isLogin ? 'Anmelden' : 'Registrieren'
              )}
            </button>

            {/* Toggle Mode */}
            <div className="text-center">
              <button
                type="button"
                onClick={toggleMode}
                className="text-sm text-orange-600 hover:text-orange-500"
              >
                {isLogin 
                  ? 'Noch kein Konto? Jetzt registrieren'
                  : 'Bereits ein Konto? Jetzt anmelden'
                }
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginScreen;